import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, ZoomIn } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import Lightbox from '../components/Lightbox';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const CirugiasSecundarias = () => {
  const lang = useLanguage();
  const t = getPageTexts('cirugiasSecundarias', lang);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const casoTexts = {
    es: {
      caseTitle: 'Caso Clínico',
      caseSubtitle: 'Corrección de Blefaroplastia Previa',
      caseDesc: 'Blefaroplastia previa con exceso de resección y retracción cicatricial. Corrección mediante cantopexia e injerto de piel.',
      before: 'Antes',
      beforeDesc: 'Retracción palpebral y exposición escleral por exceso de resección en blefaroplastia previa',
      after: 'Resultado Inmediato',
      afterDesc: 'Corrección de la retracción y restauración del cierre palpebral',
      intra: 'Intraoperatoria',
      intraDesc: 'Corrección quirúrgica mediante cantopexia e injerto de piel',
      casesTitle: 'Tratamos',
      casesSubtitle: 'Casos frecuentes',
      cases: [
        'Revisión de cicatrices antiestéticas',
        'Corrección de asimetrías postquirúrgicas',
        'Mejora de resultados insatisfactorios',
        'Complicaciones de cirugías previas',
        'Reintervenciones mamarias',
        'Revisiones de rinoplastia',
      ],
      approachTitle: 'Nuestro Enfoque',
      approachSubtitle: 'Profesionalidad y discreción',
      approachDesc1: 'Entendemos lo difícil que puede ser buscar una segunda intervención. Nuestro enfoque es completamente profesional y discreto, sin ningún tipo de juicio sobre procedimientos o profesionales anteriores. Nos centramos únicamente en encontrar la mejor solución para su caso particular.',
      approachDesc2: 'Cada caso de cirugía secundaria es único y requiere un análisis detallado. En la consulta de valoración evaluaremos las opciones disponibles y estableceremos un plan quirúrgico realista con expectativas claras.',
    },
    en: {
      caseTitle: 'Clinical Case',
      caseSubtitle: 'Previous Blepharoplasty Correction',
      caseDesc: 'Previous blepharoplasty with over-resection and scar retraction. Correction through canthoplasty and skin graft.',
      before: 'Before',
      beforeDesc: 'Eyelid retraction and scleral show due to over-resection in previous blepharoplasty',
      after: 'Immediate Result',
      afterDesc: 'Correction of retraction and restoration of eyelid closure',
      intra: 'Intraoperative',
      intraDesc: 'Surgical correction through canthoplasty and skin graft',
      casesTitle: 'We Treat',
      casesSubtitle: 'Common cases',
      cases: [
        'Revision of unsightly scars',
        'Correction of post-surgical asymmetries',
        'Improvement of unsatisfactory results',
        'Complications from previous surgeries',
        'Breast reinterventions',
        'Rhinoplasty revisions',
      ],
      approachTitle: 'Our Approach',
      approachSubtitle: 'Professionalism and discretion',
      approachDesc1: 'We understand how difficult it can be to seek a second intervention. Our approach is completely professional and discreet, without any judgment about previous procedures or professionals. We focus solely on finding the best solution for your particular case.',
      approachDesc2: 'Each secondary surgery case is unique and requires detailed analysis. In the assessment consultation we will evaluate available options and establish a realistic surgical plan with clear expectations.',
    },
    fr: {
      caseTitle: 'Cas Clinique',
      caseSubtitle: 'Correction de Blépharoplastie Antérieure',
      caseDesc: 'Blépharoplastie précédente avec excès de résection et rétraction cicatricielle. Correction par canthopexie et greffe de peau.',
      before: 'Avant',
      beforeDesc: 'Rétraction palpébrale et exposition sclérale due à une résection excessive lors d\'une blépharoplastie précédente',
      after: 'Résultat Immédiat',
      afterDesc: 'Correction de la rétraction et restauration de la fermeture palpébrale',
      intra: 'Intraopératoire',
      intraDesc: 'Correction chirurgicale par canthopexie et greffe de peau',
      casesTitle: 'Nous Traitons',
      casesSubtitle: 'Cas fréquents',
      cases: [
        'Révision de cicatrices inesthétiques',
        'Correction d\'asymétries post-chirurgicales',
        'Amélioration de résultats insatisfaisants',
        'Complications de chirurgies précédentes',
        'Réinterventions mammaires',
        'Révisions de rhinoplastie',
      ],
      approachTitle: 'Notre Approche',
      approachSubtitle: 'Professionnalisme et discrétion',
      approachDesc1: 'Nous comprenons à quel point il peut être difficile de chercher une seconde intervention. Notre approche est totalement professionnelle et discrète, sans aucun jugement sur les procédures ou professionnels précédents. Nous nous concentrons uniquement sur la recherche de la meilleure solution pour votre cas particulier.',
      approachDesc2: 'Chaque cas de chirurgie secondaire est unique et nécessite une analyse détaillée. Lors de la consultation d\'évaluation, nous évaluerons les options disponibles et établirons un plan chirurgical réaliste avec des attentes claires.',
    },
    pt: {
      caseTitle: 'Caso Clínico',
      caseSubtitle: 'Correção de Blefaroplastia Anterior',
      caseDesc: 'Blefaroplastia anterior com excesso de ressecção e retração cicatricial. Correção através de cantoplastia e enxerto de pele.',
      before: 'Antes',
      beforeDesc: 'Retração palpebral e exposição escleral por excesso de ressecção em blefaroplastia anterior',
      after: 'Resultado Imediato',
      afterDesc: 'Correção da retração e restauração do fechamento palpebral',
      intra: 'Intraoperatória',
      intraDesc: 'Correção cirúrgica através de cantoplastia e enxerto de pele',
      casesTitle: 'Tratamos',
      casesSubtitle: 'Casos frequentes',
      cases: [
        'Revisão de cicatrizes antiestéticas',
        'Correção de assimetrias pós-cirúrgicas',
        'Melhoria de resultados insatisfatórios',
        'Complicações de cirurgias anteriores',
        'Reintervenções mamárias',
        'Revisões de rinoplastia',
      ],
      approachTitle: 'Nossa Abordagem',
      approachSubtitle: 'Profissionalismo e discrição',
      approachDesc1: 'Entendemos o quão difícil pode ser buscar uma segunda intervenção. Nossa abordagem é completamente profissional e discreta, sem nenhum tipo de julgamento sobre procedimentos ou profissionais anteriores. Focamos unicamente em encontrar a melhor solução para o seu caso particular.',
      approachDesc2: 'Cada caso de cirurgia secundária é único e requer uma análise detalhada. Na consulta de avaliação, avaliaremos as opções disponíveis e estabeleceremos um plano cirúrgico realista com expectativas claras.',
    },
  };

  const ct = casoTexts[lang] || casoTexts.es;

  const [lbImages, setLbImages] = useState([]);
  const [lbIndex, setLbIndex] = useState(0);
  const [lbOpen, setLbOpen] = useState(false);
  const openLb = (imgs, i) => { setLbImages(imgs); setLbIndex(i); setLbOpen(true); };

  const abdomTexts = {
    es: {
      caseTitle: 'Caso Clínico',
      caseSubtitle: 'Abdominoplastia Fracasada — Susceptible de Reparación',
      caseDesc: 'Abdominoplastia realizada en otro servicio con pésima calidad: asimetrías, cicatrices de mala calidad, distancia del ombligo a la cicatriz muy corta. Resultado antiestético susceptible de reparación.',
      frontal: 'Vista frontal — Asimetrías y cicatrices de mala calidad',
      medicion: 'Distancia ombligo-cicatriz muy corta (medición)',
      lateral1: 'Vista lateral — Cicatrices irregulares',
      lateral2: 'Vista lateral — Deformidades cicatriciales',
      oblicua: 'Vista oblicua — Asimetrías evidentes',
      disclaimer: 'Caso real — Imágenes clínicas con fines educativos',
    },
    en: {
      caseTitle: 'Clinical Case',
      caseSubtitle: 'Failed Abdominoplasty — Amenable to Repair',
      caseDesc: 'Abdominoplasty performed at another facility with poor quality: asymmetries, poor quality scars, very short umbilicus-to-scar distance. Unaesthetic result amenable to repair.',
      frontal: 'Frontal view — Asymmetries and poor quality scars',
      medicion: 'Very short umbilicus-to-scar distance (measurement)',
      lateral1: 'Lateral view — Irregular scars',
      lateral2: 'Lateral view — Scar deformities',
      oblicua: 'Oblique view — Evident asymmetries',
      disclaimer: 'Real case — Clinical images for educational purposes',
    },
    fr: {
      caseTitle: 'Cas Clinique',
      caseSubtitle: 'Abdominoplastie Ratée — Susceptible de Réparation',
      caseDesc: 'Abdominoplastie réalisée dans un autre service de mauvaise qualité : asymétries, cicatrices de mauvaise qualité, distance ombilic-cicatrice très courte. Résultat inesthétique susceptible de réparation.',
      frontal: 'Vue frontale — Asymétries et cicatrices de mauvaise qualité',
      medicion: 'Distance ombilic-cicatrice très courte (mesure)',
      lateral1: 'Vue latérale — Cicatrices irrégulières',
      lateral2: 'Vue latérale — Déformations cicatricielles',
      oblicua: 'Vue oblique — Asymétries évidentes',
      disclaimer: 'Cas réel — Images cliniques à des fins éducatives',
    },
    pt: {
      caseTitle: 'Caso Clínico',
      caseSubtitle: 'Abdominoplastia Fracassada — Susceptível de Reparação',
      caseDesc: 'Abdominoplastia realizada em outro serviço com péssima qualidade: assimetrias, cicatrizes de má qualidade, distância do umbigo à cicatriz muito curta. Resultado antiestético susceptível de reparação.',
      frontal: 'Vista frontal — Assimetrias e cicatrizes de má qualidade',
      medicion: 'Distância umbigo-cicatriz muito curta (medição)',
      lateral1: 'Vista lateral — Cicatrizes irregulares',
      lateral2: 'Vista lateral — Deformidades cicatriciais',
      oblicua: 'Vista oblíqua — Assimetrias evidentes',
      disclaimer: 'Caso real — Imagens clínicas com fins educativos',
    },
  };
  const at = abdomTexts[lang] || abdomTexts.es;

  const abdomImgs = [
    { src: '/images/secundarias-abdominoplastia-frontal.jpeg', alt: at.frontal, caption: at.frontal },
    { src: '/images/secundarias-abdominoplastia-medicion.jpeg', alt: at.medicion, caption: at.medicion },
    { src: '/images/secundarias-abdominoplastia-lateral1.jpeg', alt: at.lateral1, caption: at.lateral1 },
    { src: '/images/secundarias-abdominoplastia-lateral2.jpeg', alt: at.lateral2, caption: at.lateral2 },
    { src: '/images/secundarias-abdominoplastia-oblicua.jpeg', alt: at.oblicua, caption: at.oblicua },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.category}</p>
              <h1 className="font-serif text-5xl md:text-6xl text-[#1C1C1A] mb-6 leading-tight">
                {t.title}
              </h1>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-lg text-[#4A4845] leading-relaxed mb-8">
                {t.heroDesc}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/contacto"
                  className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors"
                >
                  {t.requestAssessment}
                  <ArrowRight size={16} />
                </Link>
                <a 
                  href="tel:+34952764547"
                  className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors"
                >
                  <Phone size={16} />
                  {t.callNow}
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="border-4 border-[#B8975A] p-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_norro-rebranding/artifacts/r5f0lfwh_Ver%20fotos%20recientes%20%281%29.jpeg"
                  alt="Dr. Norro"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Caso Clínico */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{ct.caseTitle}</p>
            <h2 className="font-serif text-4xl text-[#1C1C1A] mb-4">{ct.caseSubtitle}</h2>
            <p className="text-[#4A4845] max-w-3xl mx-auto">
              {ct.caseDesc}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white p-4">
              <div className="border-4 border-[#B8975A] p-2">
                <div className="h-[350px] flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_norro-rebranding/artifacts/uqlrwo6f_IMG_4002.jpg"
                    alt={ct.before}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="text-center mt-4">
                <span className="inline-block bg-[#1C1C1A] text-white text-xs tracking-wider uppercase px-4 py-2">
                  {ct.before}
                </span>
                <p className="text-[#4A4845] text-sm mt-3">
                  {ct.beforeDesc}
                </p>
              </div>
            </div>
            
            <div className="bg-white p-4">
              <div className="border-4 border-[#B8975A] p-2">
                <div className="h-[350px] flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_norro-rebranding/artifacts/v2h887jl_Ver%20fotos%20recientes.jpeg"
                    alt={ct.after}
                    className="rotate-90 h-[500px] w-auto"
                  />
                </div>
              </div>
              <div className="text-center mt-4">
                <span className="inline-block bg-[#B8975A] text-white text-xs tracking-wider uppercase px-4 py-2">
                  {ct.after}
                </span>
                <p className="text-[#4A4845] text-sm mt-3">
                  {ct.afterDesc}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 max-w-md mx-auto">
            <div className="border-4 border-[#B8975A] p-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_norro-rebranding/artifacts/r5f0lfwh_Ver%20fotos%20recientes%20%281%29.jpeg"
                alt={ct.intra}
                className="w-full h-auto"
              />
            </div>
            <div className="text-center mt-4">
              <span className="inline-block bg-[#B8975A] text-white text-xs tracking-wider uppercase px-4 py-2">
                {ct.intra}
              </span>
              <p className="text-[#4A4845] text-sm mt-3">
                {ct.intraDesc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Caso Clínico 2: Abdominoplastia Fracasada */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{at.caseTitle}</p>
            <h2 className="font-serif text-4xl text-[#1C1C1A] mb-4">{at.caseSubtitle}</h2>
            <p className="text-[#4A4845] max-w-3xl mx-auto">{at.caseDesc}</p>
          </div>

          {/* Grid de 5 fotos con lightbox */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {abdomImgs.map((img, i) => (
              <div 
                key={i} 
                className={`cursor-pointer group relative ${i === 0 ? 'md:col-span-1 md:row-span-2' : ''}`}
                onClick={() => openLb(abdomImgs, i)}
              >
                <div className={`overflow-hidden bg-black border-2 border-[#B8975A]/30 hover:border-[#B8975A] transition-colors ${i === 0 ? 'h-full' : 'aspect-square'}`}>
                  <img 
                    src={img.src} 
                    alt={img.alt} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                    <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                  </div>
                </div>
                <p className="mt-2 text-text-muted text-xs text-center">{img.caption}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-text-muted text-sm mt-8">{at.disclaimer}</p>
        </div>
      </section>

      {/* Casos */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{ct.casesTitle}</p>
            <h2 className="font-serif text-4xl text-white">{ct.casesSubtitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {ct.cases && ct.cases.map((caso, index) => (
              <div key={index} className="flex items-start gap-4 p-4 border border-[#B8975A]/30">
                <div className="w-6 h-6 bg-[#B8975A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={14} className="text-[#B8975A]" />
                </div>
                <span className="text-white/80">{caso}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enfoque */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{ct.approachTitle}</p>
          <h2 className="font-serif text-4xl text-[#1C1C1A] mb-8">{ct.approachSubtitle}</h2>
          <p className="text-[#4A4845] text-lg leading-relaxed mb-8">
            {ct.approachDesc1}
          </p>
          <p className="text-[#4A4845] text-lg leading-relaxed">
            {ct.approachDesc2}
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="font-serif text-4xl text-[#1C1C1A] mb-6">
            {t.ctaTitle}
          </h2>
          <p className="text-[#4A4845] mb-10">
            {t.ctaDesc}
          </p>
          <Link 
            to="/contacto"
            className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors"
          >
            {t.contact}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      {lbOpen && lbImages.length > 0 && <Lightbox images={lbImages} currentIndex={lbIndex} isOpen={lbOpen} onClose={() => setLbOpen(false)} onNavigate={setLbIndex} />}
    </div>
  );
};

export default CirugiasSecundarias;
